/*
  Gaia Iluka — Homepage Chapter: The Harvest, full-bleed oil frame sequence
  File: assets/gi-harvest-oil-sequence.js

  REVISION 5 (Aug 2026): Harvest was rebuilt around this sequence as its
  ONE visual asset — full-bleed for the entire chapter, not just a tail
  slice behind a fourth scene. The only change this revision made HERE
  is RANGE_START/RANGE_END below, widened from the old 0.78/1: the 105
  frames now map across the section's scroll progress, so scrolling from
  the top of Harvest to the bottom scrubs frame 1 to frame 105 (and
  back, scrolling up). Everything else in this file — the canvas
  technique, the loading strategy, the cover math — is exactly what it
  was, because none of it was ever specific to the old narrow range; it
  was already just "scroll progress -> frame index."

  REVISION 6 (Aug 2026): RANGE_START moved off 0 to give the chapter a
  clean opening. See the comment on the constant itself. The animation
  is untouched — same frames, same direction, same mapping — it simply
  starts a little way into the track instead of at its very top.

  Deliberately a SEPARATE controller from assets/gi-home-harvest-scroll.js
  (which now only times the four narrative text chapters layered on top)
  rather than folded into it — this file draws into a canvas that is the
  section's own full-bleed background, independent of whatever text
  timing that other file is doing, so either can be changed without
  touching the other.

  WHY CANVAS, NOT <IMG> SWAPPING: a single persistent <canvas> element
  whose pixel content changes via drawImage() has no DOM/layout cost per
  frame change — no new element creation, no image decode-on-attach
  layout shift, nothing for the browser to reflow. Swapping an <img> src
  105 times a scroll session works, but each swap is a new decode +
  paint on a DOM node Shopify/browsers treat as "changed content"; canvas
  avoids that class of cost entirely, which matters here given how many
  distinct frames this sequence has.

  WHY NOT RAW <video> + currentTime: covered in detail in the plan this
  file implements — off-keyframe seeks stutter, reverse seeking is worse
  than forward, and it doesn't give deterministic O(1) frame lookup the
  way an indexed image array does. This file is exactly that: scroll
  progress -> integer frame index -> array lookup -> drawImage. Forward
  and reverse cost the same because it's just array indexing either way.

  DESKTOP ONLY (>=900px, prefers-reduced-motion: no-preference) — same
  compound gate as gi-home-harvest-scroll.js. Below that, or under
  reduced motion, this script never attaches: the canvas stays
  display:none (see the section's own <style> block) and the section
  falls back to a single static frame as a plain CSS background image
  instead — see the section file for that.

  LOADING STRATEGY: nothing loads until the Harvest section is within
  ~400px of the viewport (IntersectionObserver on the section root) AND
  the desktop/motion gate matches — no frames fetched on initial page
  load regardless of where Harvest sits on the page. Once triggered, a
  sparse priority pass (every 4th frame) loads first so scrubbing has
  *something* correct-ish to show almost immediately, then the remaining
  frames backfill in the background at limited concurrency. If the
  visitor scrubs to a frame that hasn't finished loading yet, the
  nearest already-loaded frame is drawn instead of leaving the canvas
  blank or stale-by-a-lot.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-gi-harvest-scroll]';
  var CANVAS_SELECTOR = '[data-gi-harvest-oil-canvas]';
  var MANIFEST_SELECTOR = '[data-gi-harvest-oil-manifest]';
  var SECTION_SELECTOR = '.gi-harvest';

  /* RANGE_START is a lead-in, not a trim. Below it the canvas is blank,
     so the chapter opens on clean cream carrying only "The Grove / The
     Harvest"; at RANGE_START frame 1 draws — which is the pour already
     entering the top of frame — and the sequence runs on from there
     exactly as before.

     It exists because the frames used to map from 0, and frame 1 has
     oil in it. Anything drawn at progress 0 is on screen while Harvest
     is still scrolling up into view, so the artwork appeared to be
     sitting under the Island chapter before the title had even
     arrived. Nothing about the animation itself changed — only when it
     starts. */
  var RANGE_START = 0.18;
  var RANGE_END = 1;

  /* Frame 1 is not a thin trickle — the sequence opens mid-pour, with a
     good deal of oil already in shot. Cutting straight from a blank
     canvas to that at RANGE_START pops, which is the same abruptness
     the lead-in exists to remove. So the canvas's own opacity is tied
     to the first sliver of scroll after RANGE_START: still scroll-
     driven, still the same frames in the same direction at the same
     rate, just not switched on in a single pixel of scroll. */
  var FADE_SPAN = 0.04;

  var PRELOAD_STRIDE = 4;
  var MAX_CONCURRENT_LOADS = 4;

  /* MOBILE: the sequence runs under 900px now, where it used to be
     gated off and a single still frame stood in for it. What that gate
     was really protecting was the payload — 105 frames is around 8MB,
     which is not a reasonable thing to pull down on a phone connection
     to animate one chapter.

     So the phone plays every third frame: 35 of the 105, roughly
     2.7MB, mapped across the same progress range at the same speed. It
     is the same pour, sampled coarser. At the pace this scrubs on a
     short track the drop between samples is small, and the machinery
     for it already existed — nearestLoadedIndex has always covered
     frames that aren't there yet.

     Desktop is unchanged and still loads all 105. */
  var MOBILE_STRIDE = 3;

  var instances = typeof WeakMap === 'function' ? new WeakMap() : null;

  function clamp01(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  function readManifest(root) {
    var script = root.querySelector(MANIFEST_SELECTOR);
    if (!script) return [];
    try {
      var urls = JSON.parse(script.textContent || '[]');
      return Array.isArray(urls) ? urls.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  function createController(root) {
    var canvas = root.querySelector(CANVAS_SELECTOR);
    var urls = readManifest(root);

    if (!canvas || !urls.length) {
      return null; // nothing to do — no manifest, no canvas, or both missing
    }

    var ctx = canvas.getContext('2d');
    var frameCount = urls.length;
    var frames = new Array(frameCount); // Image elements once loaded
    var loadedFlags = new Array(frameCount);
    var loadQueue = [];
    var activeLoads = 0;
    var lastDrawnIndex = -1;
    var lastOpacity = null;
    var lastCanvasCssW = 0;
    var lastCanvasCssH = 0;

    var desktopQuery = window.matchMedia('(min-width: 900px)');
    var motionQuery = window.matchMedia('(prefers-reduced-motion: no-preference)');

    /* Which frames this viewport is allowed to pull. Read at the moment
       loading starts rather than held as state, so a phone that starts
       the chapter narrow and a tablet that starts it wide each get the
       right set. Crossing INTO desktop later backfills (see
       ensureFullLoad); crossing the other way simply stops asking for
       more, since what is already in memory costs nothing further. */
    function frameStride() {
      return desktopQuery.matches ? 1 : MOBILE_STRIDE;
    }

    /* Snap a requested frame to the strided set the phone actually
       loads. drawIndex would fall back to the nearest loaded frame
       anyway, but it keys its short-circuit on the REQUESTED index — so
       without this the same picture is redrawn two extra times per
       step, full-screen, mid-scroll. */
    function quantize(index) {
      var stride = frameStride();
      if (stride === 1) return index;
      var snapped = Math.round(index / stride) * stride;
      return snapped > frameCount - 1 ? frameCount - 1 : snapped;
    }

    var section = root.closest ? root.closest(SECTION_SELECTOR) : null;
    var io = null;
    var scrollListening = false;
    var ticking = false;
    var loadingStarted = false;
    var loadedStride = 0;

    function sizeCanvas() {
      var rect = canvas.getBoundingClientRect();
      var cssW = Math.max(1, Math.round(rect.width));
      var cssH = Math.max(1, Math.round(rect.height));
      if (cssW === lastCanvasCssW && cssH === lastCanvasCssH) return;
      lastCanvasCssW = cssW;
      lastCanvasCssH = cssH;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
    }

    // Classic "object-fit: cover" math, drawn manually since canvas has
    // no native object-fit — keeps this visually consistent with every
    // other scene's <img>, which all use object-fit: cover.
    function drawCover(img) {
      if (!ctx || !img || !img.naturalWidth || !img.naturalHeight) return;
      var cw = canvas.width, ch = canvas.height;
      var iw = img.naturalWidth, ih = img.naturalHeight;
      var scale = Math.max(cw / iw, ch / ih);
      var dw = iw * scale, dh = ih * scale;
      // Centred horizontally, but anchored to the TOP vertically rather
      // than centred. The oil reaches row 0 of every frame (it pours in
      // from above), and this chapter now begins exactly at the bottom
      // edge of the Island frame — so a centred crop, which trims the
      // overflow equally top and bottom on a wide viewport, would cut
      // the head off the pour and leave the oil starting some way below
      // the join. Top-anchored, the first row of the frame always lands
      // on the first row of the section.
      var dx = (cw - dw) / 2, dy = 0;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, dx, dy, dw, dh);
    }

    function nearestLoadedIndex(target) {
      if (loadedFlags[target]) return target;
      for (var r = 1; r < frameCount; r++) {
        var lo = target - r, hi = target + r;
        if (lo >= 0 && loadedFlags[lo]) return lo;
        if (hi < frameCount && loadedFlags[hi]) return hi;
        if (lo < 0 && hi >= frameCount) break;
      }
      return -1;
    }

    /* The blank the lead-in shows. lastDrawnIndex is reset so the next
       real frame after it always redraws — drawIndex short-circuits on
       an unchanged index, and without this a visitor scrolling back up
       past RANGE_START and down again would land on the same index and
       be left with a cleared canvas. */
    function setOpacity(value) {
      var next = value >= 1 ? '' : String(Math.round(value * 100) / 100);
      if (next === lastOpacity) return;
      canvas.style.opacity = next;
      lastOpacity = next;
    }

    function clearCanvas() {
      setOpacity(0);
      if (lastDrawnIndex === -1) return;
      sizeCanvas();
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      lastDrawnIndex = -1;
    }

    function drawIndex(index) {
      if (index === lastDrawnIndex) return;
      var use = loadedFlags[index] ? index : nearestLoadedIndex(index);
      if (use === -1) return; // nothing loaded yet at all
      sizeCanvas();
      drawCover(frames[use]);
      lastDrawnIndex = index;
    }

    function pumpQueue() {
      while (activeLoads < MAX_CONCURRENT_LOADS && loadQueue.length) {
        var idx = loadQueue.shift();
        if (loadedFlags[idx]) continue;
        activeLoads++;
        (function (i) {
          var img = new Image();
          img.decoding = 'async';
          img.onload = function () {
            frames[i] = img;
            loadedFlags[i] = true;
            activeLoads--;
            // If the frame currently due to be shown wasn't ready
            // before, this may now let a stale draw catch up.
            pumpQueue();
          };
          img.onerror = function () {
            activeLoads--;
            pumpQueue();
          };
          img.src = urls[i];
        })(idx);
      }
    }

    function startLoading() {
      if (loadingStarted) return;
      loadingStarted = true;

      /* On a phone the eligible set is every MOBILE_STRIDE-th frame;
         on desktop it is all of them. The sparse-then-backfill order
         below runs inside whichever set that is, so scrubbing has
         something roughly right to show early either way. */
      var stride = frameStride();
      var priority = [];
      var rest = [];
      for (var i = 0; i < frameCount; i += PRELOAD_STRIDE * stride) priority.push(i);
      for (var j = 0; j < frameCount; j += stride) {
        if (j % (PRELOAD_STRIDE * stride) !== 0) rest.push(j);
      }
      loadedStride = stride;
      loadQueue = priority.concat(rest);
      pumpQueue();
    }

    /* A viewport that grows past 900px after the phone set has already
       been fetched — a rotation, a desktop window dragged wider — is
       owed the frames it skipped. Everything already in memory is kept;
       only the gaps are queued. */
    function ensureFullLoad() {
      if (!loadingStarted || loadedStride === 1) return;
      loadedStride = 1;
      for (var i = 0; i < frameCount; i++) {
        if (!loadedFlags[i]) loadQueue.push(i);
      }
      pumpQueue();
    }

    function measureAndDraw() {
      var rect = root.getBoundingClientRect();
      /* Progress runs against the track's FULL height, not the pinned
         distance (height - viewport). Measured the short way, the
         sequence finished the moment the stage unpinned, and the last
         viewport-height of the chapter — the part where it scrolls away
         and the next section rises over it — showed a frozen final
         frame. Measured this way, p reaches 1 when the track's bottom
         passes the top of the viewport, which is exactly the moment the
         next section fills the screen: the oil keeps moving all the way
         out. */
      var scrollable = rect.height;
      var p = scrollable > 0 ? clamp01(-rect.top / scrollable) : 0;
      /* OPT-IN "view" progress, for a stage that is NOT pinned (The
         Taste, Oct 2026). Measured from the root's top only, an
         unpinned band would play while it is already leaving the
         screen. data-gi-harvest-progress="view" instead runs p from the
         moment the root's top enters at the bottom of the viewport to
         the moment its bottom leaves at the top, so the pour plays
         while the band is actually on screen. The lead-in is overridable
         per root with data-gi-harvest-range-start. Roots without the
         attribute behave exactly as before. */
      var rangeStart = RANGE_START;
      if (root.getAttribute('data-gi-harvest-progress') === 'view') {
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var span = vh + rect.height;
        p = span > 0 ? clamp01((vh - rect.top) / span) : 0;
        var rs = parseFloat(root.getAttribute('data-gi-harvest-range-start'));
        if (!isNaN(rs)) rangeStart = clamp01(rs);
      }
      if (p < rangeStart) {
        clearCanvas();
        return;
      }
      setOpacity(clamp01((p - rangeStart) / FADE_SPAN));
      var local = clamp01((p - rangeStart) / (RANGE_END - rangeStart));
      var index = quantize(Math.round(local * (frameCount - 1)));
      drawIndex(index);
    }

    function onScrollOrResize() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        measureAndDraw();
      });
    }

    function attachScroll() {
      if (scrollListening) return;
      scrollListening = true;
      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize, { passive: true });
      measureAndDraw();
    }

    function detachScroll() {
      if (!scrollListening) return;
      scrollListening = false;
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      /* Inline styles don't obey media queries. If the viewport crosses
         under 900px (or reduced motion toggles) while the lead-in has
         the canvas at part opacity, that value would otherwise stick to
         a layout that never sets it. */
      setOpacity(1);
    }

    function setupObserver() {
      if (io || !section || typeof IntersectionObserver !== 'function') {
        // No IntersectionObserver support: fall back to loading
        // immediately rather than never loading at all.
        startLoading();
        return;
      }
      io = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (entries[k].isIntersecting) {
            startLoading();
            io.disconnect();
            io = null;
            break;
          }
        }
      }, { rootMargin: '400px 0px 400px 0px' });
      io.observe(section);
    }

    /* The section carries the flag, not the canvas: the mobile layout
       hides the static poster off it, and the poster has to stay up
       until there is something to replace it with. Desktop's CSS drops
       the poster on the media query alone and is not affected either
       way. */
    function setLive(on) {
      if (!section || !section.classList) return;
      section.classList[on ? 'add' : 'remove']('gi-harvest--oil-live');
    }

    function evaluate() {
      /* Width is no longer part of this gate. It used to read
         `desktopQuery.matches && motionQuery.matches`, which is what
         left the phone looking at one frozen frame while the copy
         scrolled past it. Motion preference still decides — reduced
         motion keeps the still at every width, as it always has. */
      if (motionQuery.matches) {
        setupObserver();
        attachScroll();
        /* Grown into desktop after loading the phone's sparse set. */
        if (desktopQuery.matches) ensureFullLoad();
        setLive(true);
      } else {
        detachScroll();
        if (io) { io.disconnect(); io = null; }
        setLive(false);
      }
    }

    function addMediaListener(mq, handler) {
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else if (mq.addListener) mq.addListener(handler);
    }
    function removeMediaListener(mq, handler) {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    }

    addMediaListener(desktopQuery, evaluate);
    addMediaListener(motionQuery, evaluate);
    evaluate();

    return {
      destroy: function () {
        detachScroll();
        if (io) { io.disconnect(); io = null; }
        setLive(false);
        removeMediaListener(desktopQuery, evaluate);
        removeMediaListener(motionQuery, evaluate);
      }
    };
  }

  function init(root) {
    if (!root || !instances || instances.has(root)) return;
    var controller = createController(root);
    if (controller) instances.set(root, controller);
  }

  function destroy(root) {
    if (!root || !instances) return;
    var controller = instances.get(root);
    if (!controller) return;
    controller.destroy();
    instances.delete(root);
  }

  function initWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var roots = scope.matches && scope.matches(ROOT_SELECTOR)
      ? [scope]
      : scope.querySelectorAll(ROOT_SELECTOR);
    roots.forEach ? roots.forEach(init) : Array.prototype.forEach.call(roots, init);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    initWithin(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    initWithin(evt.target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var root = evt.target && evt.target.querySelector
      ? evt.target.querySelector(ROOT_SELECTOR)
      : null;
    if (root) destroy(root);
  });
})();
